import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ApiError, api, post } from "../api";
import { demoLink, ownLink, type CallRecord, type Demo, type VisitRecord, type VisitSummary } from "../types";
import { CallModal } from "../CallModal";
import { DeleteDemo } from "../DeleteDemo";
import { PlayStop } from "../RecordingPlayer";
import { VisitsCard } from "../Visits";
import { Button, Card, CardHead, Note, Shell, StatusPill, Toggle, duration, lapsed, longDate, when } from "../ui";

/*
 * THE CALLS TABLE'S COLUMNS, from `md` up and nowhere else.
 *
 * The fixed tracks come to 460px before the summary gets a pixel, and this card sits in a
 * 768-max column inside `px-6` - so on a phone the row was far wider than the card that
 * clips it, and the two controls at the right-hand end were simply cut off. Below `md` the
 * row stacks instead (see the row itself); this is only ever the wide layout.
 *
 * `minmax(0,1fr)` and not `1fr`, because a bare `1fr` is `minmax(auto,1fr)` - a track that
 * refuses to go below its content, so one long summary pushed the whole row wide again. The
 * last track is a fixed 150px and not `auto`: `auto` would size every row to its own
 * controls, and a row with a play square would step its summary column out of line with one
 * without.
 */
const CALL_COLS = "md:grid-cols-[130px_70px_150px_minmax(0,1fr)_150px]";

function download(name: string, text: string, type: string) {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

export function LivePage() {
  const { id = "" } = useParams();
  const [demo, setDemo] = useState<Demo | null>(null);
  const [calls, setCalls] = useState<CallRecord[]>([]);
  const [visits, setVisits] = useState<{ visits: VisitRecord[]; summary: VisitSummary | null }>({ visits: [], summary: null });
  /* WHICH CALL IS ON SCREEN, and whether the press that opened it was a press of play.
   * By id and never by object: the fifteen-second poll below replaces `calls` wholesale,
   * so anything holding a CallRecord would be looking at a stale one within the minute. */
  const [open, setOpen] = useState<{ id: string; play: boolean } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [passcode, setPasscode] = useState<string | null>(null);
  const [ungated, setUngated] = useState(false);
  const [copied, setCopied] = useState(false);

  async function load() {
    try {
      const [{ demo }, { calls }, seen] = await Promise.all([
        api<{ demo: Demo }>(`/v1/demos/${id}`), api<{ calls: CallRecord[] }>(`/v1/demos/${id}/calls`),
        api<{ visits: VisitRecord[]; summary: VisitSummary }>(`/v1/demos/${id}/visits`),
      ]);
      setDemo(demo);
      setCalls(calls);
      setVisits(seen);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not load the demo.");
    }
  }

  useEffect(() => {
    void load();
    const timer = setInterval(() => void load(), 15_000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function act(action: "pause" | "extend") {
    try {
      const result = await post<{ demo: Demo }>(`/v1/demos/${id}/${action}`);
      setDemo(result.demo);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "That did not work.");
    }
  }

  /* On mints a code and shows it once; on again rotates it; off clears it and the link
   * opens ungated. None of it republishes or touches the expiry - that is the point of
   * the separate route. */
  async function setGate(on: boolean) {
    try {
      const result = await post<{ passcode: string | null; demo: Demo }>(`/v1/demos/${id}/passcode`, { on });
      setPasscode(result.passcode);
      setUngated(!on);
      setDemo(result.demo);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "That did not work.");
    }
  }

  /* Hides "have them call you" on THIS link, and nothing else - no republish, no change to
   * the expiry or the status, and the situations stay on the review screen where somebody
   * wrote them. A demo with nothing to ring about is unaffected either way: the spec is
   * what decides whether there is anything to offer, this only ever takes it away. */
  async function setRingsOut(on: boolean) {
    try {
      const result = await post<{ demo: Demo }>(`/v1/demos/${id}/outbound`, { on });
      setDemo(result.demo);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "That did not work.");
    }
  }

  async function exportAll() {
    try {
      const body = await api<{ spec: unknown; flow_md: string; calls: unknown[] }>(`/v1/demos/${id}/export`);
      const slug = demo?.slug ?? "demo";
      download(`${slug}.export.json`, JSON.stringify(body, null, 2), "application/json");
      download(`${slug}.FLOW.md`, body.flow_md, "text/markdown");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not export.");
    }
  }

  if (!demo) return <Shell crumb><main className="px-4 py-8 sm:p-10 text-[14px] text-muted">{error ?? "Loading…"}</main></Shell>;

  const link = demoLink(demo.slug);
  // Re-found on every render, so the poll flows straight through an open modal.
  const openCall = open ? calls.find((call) => call.id === open.id) ?? null : null;
  const spec = demo.published_spec ?? demo.draft_spec;
  const confirmed = demo.published_spec?.facts.length ?? 0;
  // The PUBLISHED spec's, not the draft's: what the live page describes is the demo a
  // prospect would open right now, and a situation added on Review rings nobody until
  // somebody presses Publish.
  const hasSituations = demo.published_spec?.outbound.situations.length ?? 0;
  // What the PAGE will actually do, which is both answers at once: something to ring
  // about, and a link that has not been switched off. `/api/config` computes the same.
  const ringsOut = hasSituations > 0 && !demo.outbound_off;
  // A demo past its date is still a `live` row - nothing writes 'expired' - but the
  // prospect is already being shown the closed page, so offer the date, not a pause.
  const closed = lapsed(demo.expires_at);
  const live = demo.status === "live" && !closed;

  return (
    <Shell
      crumb
      eyebrow="Step 3 of 3 · Live"
      title={<span className="flex flex-wrap items-center gap-x-2.5 gap-y-1">{demo.business_name}<StatusPill status={demo.status} pending={demo.pending_count} expiresAt={demo.expires_at} /></span>}
      actions={
        <>
          <Link to={`/demos/${id}/review`} className="inline-flex items-center h-11 px-4 border border-field bg-paper rounded-lg text-[14px] font-medium">Edit facts</Link>
          {live ? <Button onClick={() => void act("pause")}>Pause</Button> : demo.published_spec ? <Button onClick={() => void act("extend")}>{closed ? "Reopen" : "Resume"} · 90 days</Button> : null}
          <DeleteDemo demo={demo} />
        </>
      }
    >
      {error && <div className="mx-4 sm:mx-6 md:mx-10 mt-6 px-5 py-3 bg-warn-wash text-warn rounded-card text-[13px]">{error}</div>}
      {passcode && (
        <div className="mx-4 sm:mx-6 md:mx-10 mt-6 flex items-center gap-4 px-5 py-4 bg-ink text-white rounded-card flex-wrap">
          <span className="text-[12px] text-faint uppercase tracking-[0.08em]">New passcode, shown once</span>
          <span className="font-mono text-[22px] sm:text-[28px]">{passcode}</span>
          <span className="text-[13px] text-[#d9d4ca]">The old one stopped working the moment this was minted.</span>
        </div>
      )}

      <main className="flex gap-8 px-4 sm:px-6 md:px-10 py-6 sm:py-7 grow items-start flex-wrap">
        <div className="flex flex-col gap-5 w-full max-w-[768px]">
          <Card className="p-5 gap-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="text-[14px] font-semibold">Share</div>
              <div className="text-[12px] text-muted">
                {demo.expires_at ? <>Expires {longDate(demo.expires_at)} · <button type="button" className="text-ink hover:text-ok" onClick={() => void act("extend")}>extend</button></> : "Not published yet"}
              </div>
            </div>
            <div className="flex gap-2.5 flex-wrap">
              {/* Its own line on a phone, with the two buttons sharing the one under it. */}
              <div className="flex items-center grow basis-full sm:basis-auto h-11 px-3.5 border border-field rounded-lg font-mono text-[14px] bg-paper-soft min-w-0 overflow-hidden"><span className="truncate">{link}</span></div>
              <Button kind="primary" className="grow sm:grow-0 justify-center" onClick={async () => { await navigator.clipboard.writeText(link).catch(() => {}); setCopied(true); setTimeout(() => setCopied(false), 1600); }}>
                {copied ? "Copied" : "Copy link"}
              </Button>
              {/* ?me=1 marks THIS browser as ours on the demo host, for a year: its visits
                  stop counting, here and on every later load. Copy link stays clean. */}
              <a href={ownLink(demo.slug)} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center grow sm:grow-0 h-11 px-4 border border-field bg-paper rounded-lg text-[14px] font-medium" title="Opens the demo and marks this browser as yours, so your own visits are not counted">
                Open (not counted)
              </a>
            </div>
            <div className="flex gap-x-6 gap-y-3 text-[13px] flex-wrap">
              <div className="flex flex-col gap-1"><span className="text-muted text-[12px]">Passcode</span>
                <span className="flex items-center gap-2">
                  <Toggle value={demo.has_passcode} onChange={(on) => void setGate(on)} />
                  {demo.has_passcode && (
                    <>
                      <span className="font-mono text-[15px]">••••</span>
                      <button type="button" className="text-[12px] text-muted hover:text-ok" onClick={() => void setGate(true)}>rotate</button>
                    </>
                  )}
                </span></div>
              {/* Only where there is something to hide. A switch on a demo that rings
                  nobody would promise a control over something the spec decides. */}
              {hasSituations > 0 && (
                <div className="flex flex-col gap-1"><span className="text-muted text-[12px]">Outbound calling</span>
                  <span className="flex items-center gap-2">
                    <Toggle value={ringsOut} onChange={(on) => void setRingsOut(on)} />
                    <span className="text-[12px] text-muted">
                      {ringsOut
                        ? `${hasSituations} situation${hasSituations === 1 ? "" : "s"}`
                        : "hidden on the page"}
                    </span>
                  </span></div>
              )}
              <div className="flex flex-col gap-0.5"><span className="text-muted text-[12px]">Call limit</span><span>{Math.round((spec?.limits.max_call_seconds ?? 300) / 60)} min</span></div>
              <div className="flex flex-col gap-0.5"><span className="text-muted text-[12px]">Visits</span><span>{demo.visits
                ? <>{demo.visits} by {demo.visitors} {demo.visitors === 1 ? "person" : "people"} · last {when(demo.last_visit_at).toLowerCase()}</>
                : "none yet"}</span></div>
              <div className="flex flex-col gap-0.5"><span className="text-muted text-[12px]">Call attempts</span><span>{demo.opens}</span></div>
              <div className="flex flex-col gap-0.5"><span className="text-muted text-[12px]">Calls</span><span>{demo.calls} · last {when(demo.last_call_at).toLowerCase()}</span></div>
            </div>
            {ungated && !demo.has_passcode && (
              <div className="text-[12px] text-muted">No passcode now — the link alone opens the demo.</div>
            )}
          </Card>

          <Card className="p-5 gap-2.5">
            <div className="flex items-center justify-between">
              <div className="text-[14px] font-semibold">Paste into your email</div>
              <button type="button" className="text-[13px] text-muted hover:text-ok" onClick={() => void navigator.clipboard.writeText(emailCopy(demo, link, ringsOut)).catch(() => {})}>Copy</button>
            </div>
            <div className="px-4 py-3.5 bg-paper-soft rounded-lg text-[13px] leading-relaxed text-ink-soft whitespace-pre-line">{emailCopy(demo, link, ringsOut)}</div>
          </Card>

          <VisitsCard visits={visits.visits} summary={visits.summary} />

          <Card>
            <CardHead title="Calls on this demo" hint="The transcript and the caller's details for each, the moment it ends" />
            {/* Headings over a stacked list would be noise, so they go with the grid. */}
            <div className={`hidden md:grid ${CALL_COLS} gap-4 px-5 py-2.5 text-[12px] text-muted border-b border-line-soft bg-paper-soft`}>
              <div>When</div><div>Length</div><div>Which way</div><div>What the card says</div><div />
            </div>
            {calls.length === 0 && <div className="px-5 py-5 text-[13px] text-muted">No calls yet. Send the link, or ring it yourself first.</div>}
            {calls.map((call) => {
              const card = call.lead_card;
              const summary = card?.notes?.length ? card.notes.join(" ") : (card?.text?.split("\n").slice(2, 5).join(" · ") ?? "");
              /* One column for both directions. Outbound rows have no lane (there is no
                 router on the way out) and inbound ones have no situation, so two columns
                 would be two half-empty ones. */
              const which = call.direction === "outbound"
                ? <>Rang them{call.situation ? <span className="text-muted"> · {call.situation.label}</span> : null}</>
                : <>Rang in{call.lane ? <span className="text-muted"> · {call.lane.replace(/_/g, " ")}</span> : null}</>;
              return (
                <div key={call.id} className="border-b border-line-soft last:border-b-0">
                  <div className={`grid grid-cols-[minmax(0,1fr)_auto] ${CALL_COLS} gap-4 px-5 py-3.5 text-[13px] items-center`}>
                    <div className="hidden md:block">{when(call.started_at)}</div>
                    <div className="hidden md:block text-muted">{duration(call.duration_ms)}</div>
                    <div className="hidden md:block">{which}</div>
                    <div className="min-w-0">
                      {/* What the three hidden columns were saying, on one line, so a narrow
                          console loses the grid and not the facts. */}
                      <div className="md:hidden text-[12px] text-muted truncate">{when(call.started_at)} · {duration(call.duration_ms)} · {which}</div>
                      <div className="truncate" title={summary}>{summary || <span className="text-muted">{call.end_reason || "no card"}</span>}</div>
                    </div>
                    <div className="flex items-center justify-end gap-2.5">
                      {/* ABSENT RATHER THAN DISABLED on a call with no audio - a call from
                          before recordings shipped, or one where nobody spoke. A greyed-out
                          button would be a promise the row cannot keep.

                          IT OPENS THE MODAL AND STARTS THERE. The engine lives in the
                          transport, and a play control with no transport beside it is a
                          button with no clock, no scrubber and no way to stop. */}
                      {call.recording && (
                        <PlayStop
                          playing={false}
                          loading={false}
                          label={`the call from ${when(call.started_at).toLowerCase()}`}
                          onClick={() => setOpen({ id: call.id, play: true })}
                        />
                      )}
                      <button type="button" className="text-[13px] text-ok font-medium text-left" onClick={() => setOpen({ id: call.id, play: false })}>Open transcript</button>
                    </div>
                  </div>
                </div>
              );
            })}
          </Card>
        </div>

        <aside className="flex flex-col gap-4 w-full max-w-[400px]">
          <div className="flex flex-col gap-3.5 p-5 bg-ink text-white rounded-card">
            <div className="text-[12px] text-faint uppercase tracking-[0.08em]">When they bite</div>
            <div className="text-[17px] font-semibold leading-snug">Promote to a bespoke agent</div>
            <div className="text-[13px] leading-relaxed text-[#d9d4ca]">Everything here carries over. Nothing is typed twice.</div>
            <div className="flex flex-col gap-2 text-[13px] leading-relaxed">
              {["Everything on the review screen carries over as the starting point.", "The demo transcripts go in as research: what real callers actually asked.", "The build team starts from these answers instead of a blank page."].map((line, i) => (
                <div key={i} className="flex gap-2.5"><span className="text-[#8fd39a]">{i + 1}</span><span>{line}</span></div>
              ))}
            </div>
            <Button className="mt-1 !bg-white !text-ink !border-0" onClick={() => void exportAll()}>Download everything</Button>
          </div>

          <div className="flex flex-col gap-2.5 px-5 py-4 bg-paper border border-line rounded-card">
            <div className="text-[13px] font-semibold">What this demo can and cannot do</div>
            <div className="flex flex-col gap-1.5 text-[13px] leading-relaxed text-ink-soft">
              <div className="flex gap-2.5"><span className="text-ok">✓</span><span>Answers {confirmed} confirmed fact{confirmed === 1 ? "" : "s"}, takes a message, hangs up properly</span></div>
              <div className="flex gap-2.5"><span className="text-ok">✓</span><span>Will not offer {spec?.refusals.length ?? 0} thing{(spec?.refusals.length ?? 0) === 1 ? "" : "s"} out loud</span></div>
              <div className="flex gap-2.5"><span className="text-ok">✓</span><span>Follows the caller into another language</span></div>
              {ringsOut
                ? <div className="flex gap-2.5"><span className="text-ok">✓</span><span>Rings the customer for {hasSituations} situation{hasSituations === 1 ? "" : "s"}, and asks who it is speaking to first</span></div>
                : <div className="flex gap-2.5"><span className="text-faint">–</span><span>{hasSituations
                    ? "Does not ring out — switched off for this link above"
                    : "Does not ring out. Add a situation on the review screen to turn it on"}</span></div>}
              <div className="flex gap-2.5"><span className="text-faint">–</span><span>No calendar, no booking, no live prices</span></div>
              <div className="flex gap-2.5"><span className="text-faint">–</span><span>Reports to no owner dashboard yet</span></div>
            </div>
          </div>

          <Note title="Owner's own phone number">Not on a demo. A real number is wired during the bespoke build, once there is a signed owner.</Note>
        </aside>
      </main>

      {openCall && (
        <CallModal call={openCall} demoId={id} autoStart={open?.play ?? false} onClose={() => setOpen(null)} />
      )}
    </Shell>
  );
}

function emailCopy(demo: Demo, link: string, rings = false): string {
  const name = demo.business_name || "your business";
  // Most demos have no passcode: the link is the whole thing. One that has been given one
  // needs the extra sentence, or the prospect opens a code field nobody warned them about.
  const open = demo.has_passcode
    ? `Open ${link}, enter the passcode I've sent separately, and ring it`
    : `Open ${link} and ring it`;
  // The second half only where the demo actually rings out: a sentence promising a switch
  // that is not on the page is worse than a shorter email.
  const both = rings
    ? " Then flip it round with the switch above the phone and have it ring you, the way it would ring a customer."
    : "";
  return `I built a version of the assistant for ${name} so you can hear it rather than read about it. ${open} like a customer would. It knows your hours and your area, and it will tell you what it is not allowed to guess at.${both} Takes two minutes.`;
}
